/**
 * Event-catalog data hooks — the tenant's allowlist of event types.
 *
 * One list query serves all three views (catalog, review, blocked): the rows
 * differ only by `status`, so filtering client-side keeps the counts on the
 * tabs live without a second round trip. Mutations invalidate that one key.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';
import type {
  DeclaredField,
  RegisterEventTypeBody,
  TenantEventType,
  TestEventResult,
} from '../../lib/types.js';

interface CatalogLogic {
  isLoading: boolean;
  error: Error | null;
  /** Everything, every status — callers partition it themselves. */
  all: TenantEventType[];
  approved: TenantEventType[];
  held: TenantEventType[];
  blocked: TenantEventType[];
}

export const useEventCatalogLogic = (tenantId: string | undefined): CatalogLogic => {
  const query = useQuery<TenantEventType[]>({
    queryKey: queryKeys.eventCatalog(tenantId ?? ''),
    queryFn: () => apiRequest<TenantEventType[]>('/api/events/catalog'),
    enabled: Boolean(tenantId),
    // No polling: this is a configuration surface, not a stream. Held types
    // do arrive on their own, but a 30s poll re-rendered the whole page for a
    // list that changes a few times a day. Refetch-on-focus covers coming
    // back to look; mutations invalidate this key directly.
    staleTime: 60_000,
  });
  // Degrade rather than throw: this reads a wire response, and a drifted or
  // absent body must not take the whole Events page down with it.
  const all = Array.isArray(query.data) ? query.data : [];
  return {
    isLoading: query.isLoading,
    error: query.error,
    all,
    approved: all.filter((t) => t.status === 'approved'),
    held: all.filter((t) => t.status === 'held'),
    blocked: all.filter((t) => t.status === 'blocked'),
  };
};

interface CatalogMutations {
  register: (body: RegisterEventTypeBody) => Promise<TenantEventType>;
  update: (
    id: string,
    body: Partial<RegisterEventTypeBody> & {
      fields?: DeclaredField[];
      acknowledgeRemovedFields?: string[];
    },
  ) => Promise<TenantEventType>;
  dismiss: (id: string) => Promise<void>;
  block: (id: string) => Promise<TenantEventType>;
  /** Stop routing without refusing — the type goes back to Needs review. */
  disable: (id: string) => Promise<TenantEventType>;
  /** Send one synthetic event of this type, so a workflow can be built and
   *  tested before the real sender exists. */
  sendTest: (id: string) => Promise<TestEventResult>;
  isBusy: boolean;
  error: Error | null;
}

export const useEventCatalogMutations = (tenantId: string | undefined): CatalogMutations => {
  const qc = useQueryClient();
  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: queryKeys.eventCatalog(tenantId ?? '') });

  const register = useMutation({
    mutationFn: (body: RegisterEventTypeBody) =>
      apiRequest<TenantEventType>('/api/events/catalog', { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Partial<RegisterEventTypeBody> & {
        fields?: DeclaredField[];
        acknowledgeRemovedFields?: string[];
      };
    }) => apiRequest<TenantEventType>(`/api/events/catalog/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });

  const dismiss = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/api/events/catalog/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const block = useMutation({
    mutationFn: (id: string) =>
      apiRequest<TenantEventType>(`/api/events/catalog/${id}/block`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const disable = useMutation({
    mutationFn: (id: string) =>
      apiRequest<TenantEventType>(`/api/events/catalog/${id}/disable`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const sendTest = useMutation({
    mutationFn: (id: string) =>
      apiRequest<TestEventResult>(`/api/events/catalog/${id}/test`, { method: 'POST', body: {} }),
    // The catalog's counts move (received, last seen), and so does the
    // pipeline list — the test event is a real one.
    onSuccess: () => {
      invalidate();
      void qc.invalidateQueries({ queryKey: queryKeys.events(tenantId ?? '') });
    },
  });

  return {
    register: (body) => register.mutateAsync(body),
    update: (id, body) => update.mutateAsync({ id, body }),
    dismiss: (id) => dismiss.mutateAsync(id),
    block: (id) => block.mutateAsync(id),
    disable: (id) => disable.mutateAsync(id),
    sendTest: (id) => sendTest.mutateAsync(id),
    isBusy:
      register.isPending ||
      update.isPending ||
      dismiss.isPending ||
      block.isPending ||
      disable.isPending ||
      sendTest.isPending,
    error:
      register.error ??
      update.error ??
      dismiss.error ??
      block.error ??
      disable.error ??
      sendTest.error,
  };
};
